import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import {
  Container, Title, Text, Button, Group, Stack, Card,
  Table, Badge, Alert, Loader, Center, ActionIcon,
  ScrollArea, Divider
} from '@mantine/core';
import { IconRefresh, IconAlertTriangle, IconDownload, IconTrash, IconCheck, IconArrowsExchange } from '@tabler/icons-react';
import { api } from '../services/api';
import { JobTable } from '../components/JobTable';
import { formatDateDistance } from '../utils/date';
import { AutoRefreshControls } from '../components/AutoRefreshControls';
import { useAutoRefresh } from '../hooks/useAutoRefresh';

export function AdminReconcilePage() {
  const [reconcileJobId, setReconcileJobId] = useState<string | null>(null);
  const { enabled: autoRefresh, setEnabled: setAutoRefresh, refetchInterval } = useAutoRefresh({ interval: 5000 });
  const queryClient = useQueryClient();

  // Fetch cached reconciliation data (instant load)
  const { 
    data: cachedData, 
    isLoading: cachedLoading,
    refetch: refetchCached
  } = useQuery({
    queryKey: ['reconciliation', 'cached'],
    queryFn: () => api.getCachedReconciliation(),
    staleTime: 0, // Always fetch on mount
  });

  // Fetch recent reconciliation jobs
  const { 
    data: jobsData, 
    refetch: refetchJobs 
  } = useQuery({
    queryKey: ['reconciliation', 'jobs'],
    queryFn: () => api.getReconciliationJobs(),
    staleTime: 0, // Always fetch fresh data
    refetchInterval,
  });

  // Poll active reconciliation job
  const { data: activeJobData } = useQuery({
    queryKey: ['job', reconcileJobId],
    queryFn: () => reconcileJobId ? api.getJob(reconcileJobId) : null,
    enabled: !!reconcileJobId,
    refetchInterval: (query) => {
      const job = query.state.data;
      if (!job || job.status === 'completed' || job.status === 'failed') {
        return false;
      }
      return 3000;
    },
  });

  // Watch for job completion
  if (activeJobData?.status === 'completed' && reconcileJobId) {
    refetchCached();
    refetchJobs();
    setReconcileJobId(null);
    notifications.show({
      title: 'Reconciliation Complete',
      message: 'Database reconciliation has finished',
      color: 'green',
      icon: <IconCheck size={16} />,
    });
  }

  // Start reconciliation mutation
  const startReconcileMutation = useMutation({
    mutationFn: () => api.startReconciliation(),
    onSuccess: (data) => {
      setReconcileJobId(data.jobId);
      notifications.show({
        title: 'Reconciliation Queued',
        message: `Job ${data.jobId.slice(0, 8)}... has been queued`,
        color: 'blue',
      });
      refetchJobs();
    }
  });

  // Import database mutation
  const importMutation = useMutation({
    mutationFn: (params: { databaseId: string; dbClonerData: any }) => 
      api.importDatabase(params.databaseId, params.dbClonerData),
    onSuccess: (data, variables) => {
      notifications.show({
        title: 'Database Imported',
        message: `${variables.databaseId} has been imported to the portal`,
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      queryClient.invalidateQueries({ queryKey: ['databases'] });
    }
  });

  // Sync database mutation
  const syncMutation = useMutation({
    mutationFn: (params: { databaseId: string; dbClonerData: any }) => 
      api.syncDatabase(params.databaseId, params.dbClonerData),
    onSuccess: (data, variables) => {
      notifications.show({
        title: 'Database Synced',
        message: `${variables.databaseId} has been synced with db-cloner`,
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      queryClient.invalidateQueries({ queryKey: ['databases'] });
    }
  });

  // Delete from db-cloner mutation
  const deleteFromDbClonerMutation = useMutation({
    mutationFn: (databaseName: string) => 
      api.deleteDatabaseFromDbCloner(databaseName),
    onSuccess: (data, databaseName) => {
      notifications.show({
        title: 'Deletion Queued',
        message: `Job ${data.jobId.slice(0, 8)}... queued to delete ${databaseName} from db-cloner`,
        color: 'orange',
      });
      refetchJobs();
    }
  });

  // Delete from portal mutation
  const deleteFromPortalMutation = useMutation({
    mutationFn: (databaseId: string) => 
      api.deleteDatabaseFromPortal(databaseId),
    onSuccess: (_, databaseId) => {
      notifications.show({
        title: 'Database Deleted',
        message: `Database has been removed from the portal`,
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      queryClient.invalidateQueries({ queryKey: ['databases'] });
    }
  });

  const isRefreshing = startReconcileMutation.isPending || (activeJobData?.status === 'processing');
  const reconcileData = cachedData || null;

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header with Controls */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Database Reconciliation</Title>
            <Text size="sm" c="dimmed">
              Compare portal database records with db-cloner reality
              {reconcileData?.cachedAt && (
                <> • Last updated: {formatDateDistance(reconcileData.cachedAt)}</>
              )}
            </Text>
          </div>
          <AutoRefreshControls
            enabled={autoRefresh}
            onEnabledChange={setAutoRefresh}
            intervalLabel="(5s)"
          >
            <Button
              leftSection={<IconRefresh size={16} />}
              onClick={() => startReconcileMutation.mutate()}
              loading={isRefreshing}
            >
              Check Now
            </Button>
          </AutoRefreshControls>
        </Group>

        {/* Loading State (only on initial load) */}
        {cachedLoading && !reconcileData && (
          <Center py="xl">
            <Loader />
          </Center>
        )}

        {/* Reconciliation Results */}
        {reconcileData && (
          <>
            {/* Summary Card */}
            <Card withBorder>
              <Stack gap="sm">
                <Text fw={500}>Reconciliation Summary</Text>
                <Group gap="xl">
                  <div>
                    <Text size="xs" c="dimmed">In Sync</Text>
                    <Text size="lg" fw={600} c="green">{reconcileData.inSync}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">Untracked</Text>
                    <Text size="lg" fw={600} c="blue">{reconcileData.untracked?.length || 0}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">Orphaned</Text>
                    <Text size="lg" fw={600} c="orange">{reconcileData.orphaned?.length || 0}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed">Mismatched</Text>
                    <Text size="lg" fw={600} c="red">{reconcileData.mismatched?.length || 0}</Text>
                  </div>
                </Group>
              </Stack>
            </Card>

            {/* Untracked Databases */}
            {reconcileData.untracked?.length > 0 && (
              <Card withBorder>
                <Stack gap="md">
                  <Group justify="space-between">
                    <Text fw={500}>Untracked Databases (in db-cloner, not in portal)</Text>
                    <Badge color="blue">{reconcileData.untracked.length}</Badge>
                  </Group>
                  <ScrollArea h={400}>
                    <Table>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Database ID</Table.Th>
                          <Table.Th>Owner</Table.Th>
                          <Table.Th>Status</Table.Th>
                          <Table.Th>Actions</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {reconcileData.untracked.map((db: any) => (
                          <Table.Tr key={db.id}>
                            <Table.Td>{db.id}</Table.Td>
                            <Table.Td>{db.owner}</Table.Td>
                            <Table.Td><Badge>{db.status}</Badge></Table.Td>
                            <Table.Td>
                              <Group gap="xs">
                                <Button
                                  size="xs"
                                  leftSection={<IconDownload size={14} />}
                                  onClick={() => importMutation.mutate({ databaseId: db.id, dbClonerData: db })}
                                  loading={importMutation.isPending}
                                >
                                  Import to Portal
                                </Button>
                                <Button
                                  size="xs"
                                  color="red"
                                  leftSection={<IconTrash size={14} />}
                                  onClick={() => deleteFromDbClonerMutation.mutate(db.id)}
                                  loading={deleteFromDbClonerMutation.isPending}
                                >
                                  Delete from db-cloner
                                </Button>
                              </Group>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </ScrollArea>
                </Stack>
              </Card>
            )}

            {/* Orphaned Databases */}
            {reconcileData.orphaned?.length > 0 && (
              <Card withBorder>
                <Stack gap="md">
                  <Group justify="space-between">
                    <Text fw={500}>Orphaned Databases (in portal, not in db-cloner)</Text>
                    <Badge color="orange">{reconcileData.orphaned.length}</Badge>
                  </Group>
                  <Alert color="orange" icon={<IconAlertTriangle />}>
                    These databases will be auto-deleted after 7 days
                  </Alert>
                  <ScrollArea h={400}>
                    <Table>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Database Name</Table.Th>
                          <Table.Th>Status</Table.Th>
                          <Table.Th>Missing Since</Table.Th>
                          <Table.Th>Actions</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {reconcileData.orphaned.map((db: any) => (
                          <Table.Tr key={db.id}>
                            <Table.Td>{db.name || db.id}</Table.Td>
                            <Table.Td><Badge color="orange">{db.status}</Badge></Table.Td>
                            <Table.Td>{db.first_missing_at ? formatDateDistance(db.first_missing_at) : 'Just now'}</Table.Td>
                            <Table.Td>
                              <Button
                                size="xs"
                                color="red"
                                leftSection={<IconTrash size={14} />}
                                onClick={() => deleteFromPortalMutation.mutate(db.id)}
                                loading={deleteFromPortalMutation.isPending}
                              >
                                Delete from Portal
                              </Button>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </ScrollArea>
                </Stack>
              </Card>
            )}

            {/* Mismatched Databases */}
            {reconcileData.mismatched?.length > 0 && (
              <Card withBorder>
                <Stack gap="md">
                  <Group justify="space-between">
                    <Text fw={500}>Mismatched Databases (data differs between portal and db-cloner)</Text>
                    <Badge color="red">{reconcileData.mismatched.length}</Badge>
                  </Group>
                  <ScrollArea h={400}>
                    <Table>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Database Name</Table.Th>
                          <Table.Th>Differences</Table.Th>
                          <Table.Th>Actions</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {reconcileData.mismatched.map((mismatch: any) => (
                          <Table.Tr key={mismatch.portal.id}>
                            <Table.Td>{mismatch.portal.name || mismatch.portal.id}</Table.Td>
                            <Table.Td>
                              <Text size="xs" c="dimmed">{mismatch.differences.join(', ')}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Button
                                size="xs"
                                leftSection={<IconArrowsExchange size={14} />}
                                onClick={() => syncMutation.mutate({ databaseId: mismatch.portal.id, dbClonerData: mismatch.dbCloner })}
                                loading={syncMutation.isPending}
                              >
                                Sync from db-cloner
                              </Button>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </ScrollArea>
                </Stack>
              </Card>
            )}
          </>
        )}

        {/* Job History Section */}
        <Divider />
        <div>
          <Group justify="space-between" mb="md">
            <Text fw={500} size="lg">Recent Jobs</Text>
            <ActionIcon variant="subtle" onClick={() => refetchJobs()}>
              <IconRefresh size={18} />
            </ActionIcon>
          </Group>
          {jobsData && jobsData.length > 0 ? (
            <JobTable 
              jobs={jobsData}
              showEnvColumn={false}
              showUserColumn={true}
              pageSize={10}
            />
          ) : (
            <Text c="dimmed" ta="center" py="xl">No recent jobs</Text>
          )}
        </div>
      </Stack>
    </Container>
  );
}
